import React, { useState, useEffect, useMemo } from 'react';
import {
  createJobListing,
  getJobListings,
  getVolunteerJobListings,
  updateJobListing,
  getListingApplications,
} from '../../../api/programmesApi';

const STATUS_META = {
  pending_approval: { label: 'Pending Approval', cls: 'bb-badge', style: { background: '#FEF3C7', color: '#92400E' } },
  live:             { label: 'Live',             cls: 'bb-badge bb-badge--green' },
  paused:           { label: 'Paused',           cls: 'bb-badge bb-badge--gray' },
  closed:           { label: 'Closed',           cls: 'bb-badge bb-badge--gray' },
  rejected:         { label: 'Rejected',         cls: 'bb-badge', style: { background: '#FEE2E2', color: '#991B1B' } },
};

/* ─── Tab: Post a Job ───────────────────────────────────────────── */
function PostJob({ orgId, volunteerId, volunteerName }) {
  const BLANK = { title: '', organisation: '', jobType: '', description: '', requirements: '', deadline: '' };
  const [form, setForm]       = useState(BLANK);
  const [saving, setSaving]   = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]     = useState('');

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await createJobListing(orgId, form, volunteerId, volunteerName);
      setSubmitted(true);
      setForm(BLANK);
    } catch (err) {
      setError(err.message || 'Failed to submit listing.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bb-vol-section">
      <div className="bb-alert bb-alert--info" style={{ marginBottom: 16 }}>
        Your personal contact details will not be displayed publicly. All listings are reviewed by admin before going live.
      </div>
      {submitted && (
        <div className="bb-alert bb-alert--info" style={{ marginBottom: 16 }}>
          Listing submitted for admin approval.
        </div>
      )}
      {error && <div className="bb-alert bb-alert--error" style={{ marginBottom: 16 }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div className="bb-form-row">
          <div className="bb-form-group">
            <label>Job Title <span style={{ color: '#DC2626' }}>*</span></label>
            <input className="bb-input" name="title" value={form.title} onChange={handleChange} placeholder="e.g. Part-time Admin Coordinator" required />
          </div>
          <div className="bb-form-group">
            <label>Organisation <span style={{ color: '#DC2626' }}>*</span></label>
            <input className="bb-input" name="organisation" value={form.organisation} onChange={handleChange} placeholder="Hiring organisation name" required />
          </div>
        </div>
        <div className="bb-form-row">
          <div className="bb-form-group">
            <label>Job Type <span style={{ color: '#DC2626' }}>*</span></label>
            <select className="bb-select" name="jobType" value={form.jobType} onChange={handleChange} required>
              <option value="">— select type —</option>
              <option value="full-time">Full-time</option>
              <option value="part-time">Part-time</option>
              <option value="freelance">Freelance</option>
              <option value="flexible">Flexible</option>
            </select>
          </div>
          <div className="bb-form-group">
            <label>Application Deadline</label>
            <input className="bb-input" type="date" name="deadline" value={form.deadline} onChange={handleChange} />
          </div>
        </div>
        <div className="bb-form-group">
          <label>Job Description <span style={{ color: '#DC2626' }}>*</span></label>
          <textarea className="bb-textarea" name="description" rows={4} value={form.description} onChange={handleChange} placeholder="Describe the role, responsibilities, and what the ideal candidate looks like…" required />
        </div>
        <div className="bb-form-group">
          <label>Requirements</label>
          <textarea className="bb-textarea" name="requirements" rows={3} value={form.requirements} onChange={handleChange} placeholder="List any qualifications, experience, or skills required…" />
        </div>
        <button type="submit" className="bb-btn-primary" style={{ background: '#4338CA' }} disabled={saving}>
          {saving ? 'Submitting…' : 'Submit Listing'}
        </button>
      </form>
    </div>
  );
}


/* ─── Applicants panel (inline, per listing) ────────────────────── */
function ApplicantsPanel({ orgId, listingId }) {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    setLoading(true);
    getListingApplications(orgId, listingId)
      .then(data => setApplicants(data))
      .catch(() => setApplicants([]))
      .finally(() => setLoading(false));
  }, [orgId, listingId]);

  if (loading) return (
    <div className="bb-loading-row" style={{ padding: '12px 0' }}>
      <div className="bb-loading-spinner" /><span>Loading applicants…</span>
    </div>
  );

  return applicants.length === 0 ? (
    <div className="bb-empty-state" style={{ margin: '8px 0', fontSize: 13 }}>No applications received yet.</div>
  ) : (
    <table className="bb-table" style={{ marginTop: 8 }}>
      <thead>
        <tr>
          <th>#</th>
          <th>Name</th>
          <th>Email</th>
          <th>Applied</th>
          <th>Covering Letter</th>
          <th>Resume / CV</th>
        </tr>
      </thead>
      <tbody>
        {applicants.map((a, i) => (
          <tr key={a.id}>
            <td>{i + 1}</td>
            <td>{a.memberName}</td>
            <td>{a.memberEmail}</td>
            <td style={{ fontSize: 12, color: '#6B7280' }}>
              {a.createdAt?.toDate ? a.createdAt.toDate().toLocaleDateString() : '—'}
            </td>
            <td>
              {a.coveringLetterUrl
                ? <a href={a.coveringLetterUrl} target="_blank" rel="noreferrer" style={{ color: '#4338CA', fontSize: 13, whiteSpace: 'nowrap' }}>📄 {a.coveringLetterFileName || 'Open'}</a>
                : <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>}
            </td>
            <td>
              {a.resumeUrl
                ? <a href={a.resumeUrl} target="_blank" rel="noreferrer" style={{ color: '#4338CA', fontSize: 13, whiteSpace: 'nowrap' }}>📋 {a.resumeFileName || 'Open'}</a>
                : <span style={{ color: '#9CA3AF', fontSize: 13 }}>—</span>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ─── Tab: My Listings & Applicants ─────────────────────────────── */
function MyListingsAndApplicants({ orgId, volunteerId }) {
  const [listings, setListings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [editing, setEditing]     = useState(null);
  const [editForm, setEditForm]   = useState({});
  const [expanded, setExpanded]   = useState(null); // listing id with open applicants
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');

  const load = () => {
    setLoading(true);
    getVolunteerJobListings(orgId, volunteerId)
      .then(data => setListings(data))
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [orgId, volunteerId]); // eslint-disable-line

  const startEdit = (listing) => {
    setEditing(listing.id);
    setEditForm({ title: listing.title, description: listing.description, requirements: listing.requirements || '', deadline: listing.deadline || '' });
  };

  const saveEdit = async (id) => {
    setSaving(true);
    try {
      await updateJobListing(orgId, id, editForm);
      setMsg('Listing updated.');
      setEditing(null);
      load();
    } catch { setMsg('Error saving changes.'); }
    finally { setSaving(false); }
  };

  const handleStatus = async (id, status) => {
    setSaving(true);
    try {
      await updateJobListing(orgId, id, { status });
      setMsg(`Listing ${status}.`);
      load();
    } catch { setMsg('Error updating listing.'); }
    finally { setSaving(false); }
  };

  const toggleApplicants = (id) => setExpanded(prev => prev === id ? null : id);

  if (loading) return (
    <div className="bb-loading-row">
      <div className="bb-loading-spinner" /><span>Loading listings…</span>
    </div>
  );

  return (
    <div className="bb-vol-section">
      <div className="bb-alert bb-alert--info" style={{ marginBottom: 16 }}>
        Applicant contact details are visible to you as the poster only.
      </div>
      {msg && <div className="bb-alert bb-alert--info" style={{ marginBottom: 12 }}>{msg}</div>}
      {listings.length === 0 ? (
        <div className="bb-empty-state">You have not posted any listings yet.</div>
      ) : (
        <table className="bb-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Applicants</th>
              <th>Deadline</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {listings.map(l => {
              const meta    = STATUS_META[l.status] || { label: l.status, cls: 'bb-badge' };
              const canEdit = l.status === 'live' || l.status === 'paused';
              const appCount = (l.applications || []).length;
              const isExpanded = expanded === l.id;
              return (
                <React.Fragment key={l.id}>
                  <tr>
                    <td style={{ fontWeight: 500 }}>{l.title}</td>
                    <td style={{ textTransform: 'capitalize' }}>{l.jobType || '—'}</td>
                    <td><span className={meta.cls} style={meta.style}>{meta.label}</span></td>
                    <td>
                      <button
                        onClick={() => toggleApplicants(l.id)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#4338CA', fontWeight: 600, fontSize: 13, padding: 0,
                          display: 'flex', alignItems: 'center', gap: 4,
                        }}
                      >
                        {appCount} applicant{appCount !== 1 ? 's' : ''}
                        <span style={{ fontSize: 10 }}>{isExpanded ? '▲' : '▼'}</span>
                      </button>
                    </td>
                    <td>{l.deadline || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {canEdit && (
                          <button
                            className="bb-btn-outline-sm"
                            style={{ borderColor: '#4338CA', color: '#4338CA' }}
                            onClick={() => editing === l.id ? setEditing(null) : startEdit(l)}
                          >
                            {editing === l.id ? 'Cancel' : 'Edit'}
                          </button>
                        )}
                        {l.status === 'live' && (
                          <button className="bb-btn-outline-sm" onClick={() => handleStatus(l.id, 'paused')}>Pause</button>
                        )}
                        {l.status === 'paused' && (
                          <button
                            className="bb-btn-sm"
                            style={{ background: '#4338CA', color: '#fff' }}
                            onClick={() => handleStatus(l.id, 'live')}
                          >
                            Reactivate
                          </button>
                        )}
                        {l.status !== 'closed' && (
                          <button className="bb-btn-danger-sm" onClick={() => handleStatus(l.id, 'closed')}>Close</button>
                        )}
                        {l.status === 'closed' && (
                          <span className="bb-hint" style={{ fontSize: 11 }}>Closed</span>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Inline applicants panel */}
                  {isExpanded && (
                    <tr>
                      <td colSpan={6} style={{ background: '#F9FAFB', padding: '12px 18px', borderTop: '1px solid #E5E7EB' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                          Applicants for "{l.title}"
                        </div>
                        <ApplicantsPanel orgId={orgId} listingId={l.id} />
                      </td>
                    </tr>
                  )}

                  {/* Inline edit panel */}
                  {editing === l.id && (
                    <tr>
                      <td colSpan={6} style={{ background: '#F9FAFB', padding: 16, borderTop: '1px solid #E5E7EB' }}>
                        <div className="bb-form-row">
                          <div className="bb-form-group">
                            <label>Title</label>
                            <input className="bb-input" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                          </div>
                          <div className="bb-form-group">
                            <label>Deadline</label>
                            <input className="bb-input" type="date" value={editForm.deadline} onChange={e => setEditForm(f => ({ ...f, deadline: e.target.value }))} />
                          </div>
                        </div>
                        <div className="bb-form-group">
                          <label>Description</label>
                          <textarea className="bb-textarea" rows={3} value={editForm.description} onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                        </div>
                        <div className="bb-form-group">
                          <label>Requirements</label>
                          <textarea className="bb-textarea" rows={2} value={editForm.requirements} onChange={e => setEditForm(f => ({ ...f, requirements: e.target.value }))} />
                        </div>
                        <button className="bb-btn-primary" style={{ background: '#4338CA' }} disabled={saving} onClick={() => saveEdit(l.id)}>
                          {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── Root ──────────────────────────────────────────────────────── */
const JOB_TYPE_OPTIONS = ['full-time', 'part-time', 'freelance', 'flexible', 'volunteer'];

const LISTING_STATUS_META = {
  live:   { label: 'Live',   cls: 'bb-badge bb-badge--green' },
  paused: { label: 'Paused', cls: 'bb-badge bb-badge--gray'  },
  closed: { label: 'Closed', cls: 'bb-badge bb-badge--gray'  },
};

const fmtListingDate = (ts) => {
  if (!ts) return '—';
  const d = ts?.toDate ? ts.toDate() : new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const sortIcon = (dir) => dir === 'asc' ? ' ▲' : dir === 'desc' ? ' ▼' : ' ⇅';

function ViewAvailableJobListings({ orgId }) {
  const [listings,  setListings]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [appsCache, setAppsCache] = useState({});
  const [viewItem,  setViewItem]  = useState(null);

  const [typeFilter,     setTypeFilter]     = useState('all');
  const [postedByFilter, setPostedByFilter] = useState('all');
  const [deadlineSort,   setDeadlineSort]   = useState(null);
  const [appSort,        setAppSort]        = useState(null);

  useEffect(() => {
    setLoading(true);
    getJobListings(orgId, 'live')
      .then(async (data) => {
        setListings(data);
        const counts = {};
        await Promise.all(data.map(async l => {
          try { const apps = await getListingApplications(orgId, l.id); counts[l.id] = (apps || []).length; }
          catch { counts[l.id] = 0; }
        }));
        setAppsCache(counts);
      })
      .catch(() => setListings([]))
      .finally(() => setLoading(false));
  }, [orgId]);

  const postedByOptions = useMemo(() =>
    [...new Set(listings.map(l => l.postedByName).filter(Boolean))].sort()
  , [listings]);

  const filtered = useMemo(() => {
    let rows = listings.filter(l => {
      if (typeFilter     !== 'all' && l.jobType      !== typeFilter)     return false;
      if (postedByFilter !== 'all' && l.postedByName !== postedByFilter) return false;
      return true;
    });
    if (appSort) {
      rows = [...rows].sort((a, b) => {
        const diff = (appsCache[a.id] ?? 0) - (appsCache[b.id] ?? 0);
        return appSort === 'asc' ? diff : -diff;
      });
    } else if (deadlineSort) {
      rows = [...rows].sort((a, b) => {
        const da = a.deadline ? new Date(a.deadline?.toDate ? a.deadline.toDate() : a.deadline) : new Date(0);
        const db = b.deadline ? new Date(b.deadline?.toDate ? b.deadline.toDate() : b.deadline) : new Date(0);
        return deadlineSort === 'asc' ? da - db : db - da;
      });
    }
    return rows;
  }, [listings, typeFilter, postedByFilter, appSort, deadlineSort, appsCache]);

  const cycleSort = (current, setter, otherSetter) => {
    otherSetter(null);
    setter(current === null ? 'asc' : current === 'asc' ? 'desc' : null);
  };

  return (
    <div className="bb-vol-section">

      {/* Detail modal */}
      {viewItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', borderRadius: 12, maxWidth: 580, width: '100%', boxShadow: '0 24px 72px rgba(0,0,0,0.22)', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ background: '#4338CA', borderRadius: '12px 12px 0 0', padding: '18px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 6 }}>{viewItem.title}</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', borderRadius: 20, fontSize: 12, padding: '2px 10px', fontWeight: 500, textTransform: 'capitalize' }}>{viewItem.jobType || '—'}</span>
                  <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', borderRadius: 20, fontSize: 12, padding: '2px 10px' }}>{viewItem.organisation || '—'}</span>
                </div>
              </div>
              <button onClick={() => setViewItem(null)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontSize: 13, flexShrink: 0, marginLeft: 12 }}>
                ✕ Close
              </button>
            </div>

            <div style={{ padding: '20px 24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: 13, background: '#F9FAFB', borderRadius: 8, padding: '14px 16px', marginBottom: 20 }}>
                <div><div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Company</div><div style={{ fontWeight: 600, color: '#1F2937' }}>{viewItem.organisation || '—'}</div></div>
                <div><div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Job Type</div><div style={{ fontWeight: 600, color: '#1F2937', textTransform: 'capitalize' }}>{viewItem.jobType || '—'}</div></div>
                <div><div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Posted By</div><div style={{ fontWeight: 600, color: '#1F2937' }}>{viewItem.postedByName || viewItem.postedBy || '—'}</div></div>
                <div><div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Date Posted</div><div style={{ fontWeight: 600, color: '#1F2937' }}>{fmtListingDate(viewItem.createdAt)}</div></div>
                <div><div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Application Deadline</div><div style={{ fontWeight: 600, color: viewItem.deadline ? '#DC2626' : '#1F2937' }}>{fmtListingDate(viewItem.deadline)}</div></div>
                <div><div style={{ fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 2 }}>Status</div>{(() => { const m = LISTING_STATUS_META[viewItem.status] || LISTING_STATUS_META.live; return <span className={m.cls}>{m.label}</span>; })()}</div>
              </div>

              {viewItem.description && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Job Description</div>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{viewItem.description}</p>
                </div>
              )}

              {viewItem.requirements && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Experience & Requirements</div>
                  <p style={{ fontSize: 13, color: '#374151', margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{viewItem.requirements}</p>
                </div>
              )}

              {(viewItem.contactDetails || viewItem.postedByName) && (
                <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#4338CA', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>How to Apply</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 20px', fontSize: 13 }}>
                    <div><div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Contact</div><div style={{ fontWeight: 600, color: '#1F2937' }}>{viewItem.contactDetails?.name || viewItem.postedByName || '—'}</div></div>
                    {viewItem.contactDetails?.email && <div><div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Email</div><div style={{ fontWeight: 600, color: '#4338CA' }}>{viewItem.contactDetails.email}</div></div>}
                    {viewItem.contactDetails?.phone && <div><div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 2 }}>Phone</div><div style={{ fontWeight: 600, color: '#1F2937' }}>{viewItem.contactDetails.phone}</div></div>}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18, alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Type:</label>
          <select className="bb-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }}>
            <option value="all">All types</option>
            {JOB_TYPE_OPTIONS.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label style={{ fontSize: 13, color: '#6B7280', whiteSpace: 'nowrap' }}>Posted By:</label>
          <select className="bb-select" value={postedByFilter} onChange={e => setPostedByFilter(e.target.value)} style={{ fontSize: 13, padding: '5px 8px' }}>
            <option value="all">All</option>
            {postedByOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="bb-loading-row"><div className="bb-loading-spinner" /><span>Loading listings…</span></div>
      ) : filtered.length === 0 ? (
        <div className="bb-empty-state">
          {listings.length === 0 ? 'No live job listings at the moment.' : 'No listings match the selected filters.'}
        </div>
      ) : (
        <table className="bb-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Organisation</th>
              <th>Type</th>
              <th>Posted By</th>
              <th>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 'inherit', color: 'inherit', whiteSpace: 'nowrap' }}
                  onClick={() => cycleSort(appSort, setAppSort, setDeadlineSort)}>
                  Applications{sortIcon(appSort)}
                </button>
              </th>
              <th>
                <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700, padding: 0, fontSize: 'inherit', color: 'inherit', whiteSpace: 'nowrap' }}
                  onClick={() => cycleSort(deadlineSort, setDeadlineSort, setAppSort)}>
                  Deadline{sortIcon(deadlineSort)}
                </button>
              </th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => {
              const meta = LISTING_STATUS_META[l.status] || LISTING_STATUS_META.live;
              return (
                <tr key={l.id}>
                  <td>
                    <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4338CA', fontWeight: 600, padding: 0, textAlign: 'left' }}
                      onClick={() => setViewItem(l)}>{l.title}</button>
                  </td>
                  <td>{l.organisation || '—'}</td>
                  <td style={{ textTransform: 'capitalize' }}>{l.jobType || '—'}</td>
                  <td>{l.postedByName || l.postedBy || '—'}</td>
                  <td>{appsCache[l.id] ?? '—'}</td>
                  <td>{fmtListingDate(l.deadline)}</td>
                  <td><span className={meta.cls}>{meta.label}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function JobBoardVolunteer({ orgId, volunteerId, volunteerName, activeTab }) {
  return (
    <div className="bb-vol-tool">
      <div className="bb-vol-tool-header">
        <h2>Job Board</h2>
      </div>
      {activeTab === 'Post A Job'                      && <PostJob orgId={orgId} volunteerId={volunteerId} volunteerName={volunteerName} />}
      {activeTab === 'My Job Listings & Applicants'     && <MyListingsAndApplicants orgId={orgId} volunteerId={volunteerId} />}
      {activeTab === 'View Available Job Listings'     && <ViewAvailableJobListings orgId={orgId} />}
    </div>
  );
}

export default JobBoardVolunteer;
